import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserFacingError } from '../utils/errors';
import {
  needsEmailVerification,
  peekPostVerifyPath,
  POST_VERIFY_PATH_KEY,
  sendAccountVerificationEmail,
  verificationEmailSentRecently,
} from '../utils/emailVerification';
import MarketingSurface from '../components/MarketingSurface';
import { marketingColor, marketingFont } from '../theme/marketingTokens';

export const VerifyEmailPage: React.FC = () => {
  const { currentUser, logout, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const nextPathRef = useRef(peekPostVerifyPath());
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const continueIfVerified = async (): Promise<boolean> => {
    if (!currentUser) return false;
    await currentUser.reload();
    await currentUser.getIdToken(true);
    if (!currentUser.emailVerified) return false;
    try {
      await updateUserProfile({ emailVerified: true });
    } catch (profileError) {
      // The Auth claim is the gate. The profile flag is only what the admin list shows.
      console.error('Could not update the profile verification flag:', profileError);
    }
    sessionStorage.removeItem(POST_VERIFY_PATH_KEY);
    navigate(nextPathRef.current, { replace: true });
    return true;
  };

  useEffect(() => {
    if (currentUser && !needsEmailVerification(currentUser)) {
      sessionStorage.removeItem(POST_VERIFY_PATH_KEY);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !needsEmailVerification(currentUser)) return;
    let cancelled = false;

    (async () => {
      try {
        const verified = await continueIfVerified();
        if (cancelled || verified) return;
      } catch {
        // Keep the signed-in user we already have and offer a resend.
      }
      if (cancelled) return;
      if (verificationEmailSentRecently()) {
        setNotice('We sent a verification link to your inbox.');
        return;
      }
      setSending(true);
      try {
        await sendAccountVerificationEmail(currentUser);
        if (!cancelled) setNotice('We sent a verification link to your inbox.');
      } catch (err) {
        if (!cancelled) {
          setError(getUserFacingError(err, 'Could not send the verification email. Please try again.'));
        }
      } finally {
        if (!cancelled) setSending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // currentUser identity is enough; continueIfVerified closes over the latest profile writer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login?redirect=/verify-email" replace />;
  }

  if (!needsEmailVerification(currentUser)) {
    return <Navigate to={nextPathRef.current} replace />;
  }

  const handleResend = async () => {
    setError('');
    setNotice('');
    setSending(true);
    try {
      await sendAccountVerificationEmail(currentUser);
      setNotice('We sent another verification link to your inbox.');
    } catch (err) {
      setError(getUserFacingError(err, 'Could not send the verification email. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const handleConfirmed = async () => {
    setError('');
    setChecking(true);
    try {
      const verified = await continueIfVerified();
      if (!verified) {
        setError('Open the link in your inbox, then try again.');
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Could not check verification. Please try again.'));
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <MarketingSurface>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper sx={{ padding: 4, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/logo-white.svg"
                  alt="Motzu Labs"
                  style={{
                    height: '28px',
                    width: 'auto',
                    maxWidth: '160px',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<span style="font-family: ${marketingFont.display}; font-weight: 700; font-size: 1.25rem; color: ${marketingColor.text};">Motzu Labs</span>`;
                    }
                  }}
                />
              </Box>
              <Typography component="h1" variant="h5" gutterBottom>
                Verify your email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                We sent a link to {currentUser.email}. Open it to verify your account, then continue.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}
              {notice && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {notice}
                </Alert>
              )}
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
                onClick={handleConfirmed}
                disabled={checking || sending}
              >
                {checking ? <CircularProgress size={24} /> : "I've verified"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={handleResend}
                disabled={checking || sending}
              >
                {sending ? <CircularProgress size={24} /> : 'Resend email'}
              </Button>
              <Button fullWidth variant="text" onClick={handleSignOut} disabled={checking}>
                Sign out
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </MarketingSurface>
  );
};
